import React from 'react';
import {
  Cell,
  Label,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';

import styled from '@emotion/styled';
import { RechartsDevtools } from '@recharts/devtools';

interface DoughnutChartData {
  [key: string]: string | number | undefined;
  name: string;
  value: number;
  fill?: string;
}

interface DoughnutChartProps {
  data: DoughnutChartData[];
  dataKey?: string;
  nameKey?: string;
  aspect?: number;
  innerRadius?: number | string;
  outerRadius?: number | string;
  isAnimationActive?: boolean;
  showLegend?: boolean;
  tooltipLabel?: string;
  centerLabel?: string;
  centerValue?: string | number;
}

const defaultColors = ['#5B91F5', '#FCCB34', '#E95B7B', '#6FD195', '#8E7CF4'];

const DoughnutChart: React.FC<DoughnutChartProps> = ({
  data,
  dataKey = 'value',
  nameKey = 'name',
  aspect = 1.618,
  innerRadius = '60%',
  outerRadius = '85%',
  isAnimationActive = true,
  showLegend = true,
  tooltipLabel = 'Value',
  centerLabel,
  centerValue,
}) => {
  const safeData = Array.isArray(data) ? data : [];

  const hasPositiveData = safeData.some((item) => {
    const value = Number(item[dataKey] ?? 0);
    return value > 0;
  });

  const chartData = hasPositiveData
    ? safeData
    : [
        {
          name: 'No Data',
          value: 1,
          fill: '#E6EAF2',
        },
      ];

  const derivedCenterValue =
    centerValue !== undefined
      ? centerValue
      : hasPositiveData
        ? safeData.reduce((sum, item) => sum + Number(item[dataKey] || 0), 0)
        : 0;

  return (
    <ResponsiveContainer width='100%' aspect={aspect} maxHeight={500}>
      <PieChart>
        <Pie
          data={chartData}
          dataKey={dataKey}
          nameKey={nameKey}
          cx='50%'
          cy='50%'
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          paddingAngle={1}
          isAnimationActive={isAnimationActive}
        >
          {chartData.map((entry, index) => (
            <Cell
              key={`${entry.name}-${index}`}
              fill={entry.fill || defaultColors[index % defaultColors.length]}
            />
          ))}
          {(centerLabel || centerValue !== undefined) && (
            <Label
              content={({ viewBox }: any) => {
                const x = viewBox?.cx || 0;
                const y = viewBox?.cy || 0;
                const outerRadius = viewBox?.outerRadius || 0;

                const clamp = (value: number, min: number, max: number) =>
                  Math.max(min, Math.min(max, value));

                const labelFontSize = clamp(outerRadius * 0.02, 18, 48);
                const valueFontSize = clamp(outerRadius * 1, 36, 120);
                const labelOffsetY = labelFontSize * 0.25;
                const valueOffsetY = valueFontSize * 0.32;

                return (
                  <g>
                    {centerLabel && (
                      <text
                        x={x}
                        y={y - labelOffsetY}
                        textAnchor='middle'
                        fill='#41415A'
                        fontSize={labelFontSize}
                        fontWeight='600'
                      >
                        {centerLabel}
                      </text>
                    )}
                    <text
                      x={x}
                      y={y + valueOffsetY}
                      textAnchor='middle'
                      fill='#1F2130'
                      fontSize={valueFontSize}
                      fontWeight='700'
                    >
                      {derivedCenterValue}
                    </text>
                  </g>
                );
              }}
            />
          )}
        </Pie>
        <Tooltip content={<CustomTooltip tooltipLabel={tooltipLabel} />} />
        {showLegend && (
          <Legend
            iconType='circle'
            wrapperStyle={{
              paddingTop: '20px',
              fontSize: '14px',
            }}
          />
        )}
        <RechartsDevtools />
      </PieChart>
    </ResponsiveContainer>
  );
};

export default DoughnutChart;

interface CustomTooltipProps {
  active?: boolean;
  payload?: any[];
  tooltipLabel?: string;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({
  active,
  payload,
  //   tooltipLabel,
}) => {
  if (active && payload && payload.length) {
    return (
      <TooltipContainer>
        <TooltipItem>
          <TooltipRow>
            {/* <ColorDot color={payload[0].color} /> */}
            <TooltipLabel>{payload[0].name}:</TooltipLabel>
            <TooltipValue>{payload[0].value}</TooltipValue>
          </TooltipRow>
        </TooltipItem>
        {/* <TooltipMetricLabel>{tooltipLabel}</TooltipMetricLabel> */}
      </TooltipContainer>
    );
  }
  return null;
};

const TooltipContainer = styled.div`
  background: white;
  border-radius: 6px;
  padding: 4px 12px;
  box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TooltipItem = styled.div`
  display: flex;
  align-items: center;
`;

const TooltipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

// const ColorDot = styled.span<{ color: string }>`
//   width: 8px;
//   height: 8px;
//   border-radius: 50%;
//   background-color: ${(props) => props.color};
//   flex-shrink: 0;
// `;

const TooltipLabel = styled.span`
  font-size: 10px;
  color: #6b7280;
  font-weight: 400;
`;

const TooltipValue = styled.span`
  font-size: 12px;
  color: #000000;
  font-weight: 500;
`;

// const TooltipMetricLabel = styled.span`
//   font-size: 10px;
//   color: #6b7280;
//   font-weight: 400;
// `;
